import React from 'react';
import classNames from 'classnames';
import { FieldPathByValue, UseFormReturn } from 'react-hook-form';
import BulletedTextarea from '../BulletedTextarea';
import * as styles from './CriteriaFieldsets.module.scss';
import CriteriaSectionHeader from './CriteriaSectionHeader';
import { FormLabel, TextArea } from 'components/core';
import { Stack } from 'components/layout';
import { useUniqueID } from 'hooks/useAccessibleName';
import {
  Criteria,
  CriteriaCategory,
  CriteriaFramework,
  getCategoryKeys,
  getFrameworkCategoriesWithFallbackToPicos,
} from 'concepts/Criteria/framework/CriteriaFramework';

// The criteria fields are always string[] bullet lists on the host form.
export type BulletPath<TValues extends Criteria> = FieldPathByValue<
  TValues,
  string[] | undefined
>;

export type CriteriaFieldsetsLayout = 'default' | 'sidebar';

const joinOtherItems = (items: string[]): string[] => {
  const nonEmpty = items.filter((item) => item !== '');

  return nonEmpty.length > 1 ? [nonEmpty.join('\n')] : nonEmpty;
};

// Seed only with server-sanitised criteria: BulletedTextarea renders the
// values as trusted HTML.
export const buildCriteriaValues = (
  framework?: CriteriaFramework,
  savedCriteria: Criteria = {}
): Criteria => {
  const values: Criteria = {};

  // Seed form state from the union of the current framework's visible
  // categories and every category present in the saved data. Categories that
  // belong to other frameworks are not rendered, but keeping them in form
  // state preserves their data so switching to a wider framework (or saving)
  // does not overwrite populated rows with empty arrays (PIER-882).
  const keys = new Set<CriteriaCategory>([
    ...getCategoryKeys(framework),
    ...(Object.keys(savedCriteria) as CriteriaCategory[]),
  ]);

  keys.forEach((key) => {
    // the include/exclude keys refer to the include/exclude criteria we get from the database
    const include = savedCriteria[key]?.include ?? [];
    const exclude = savedCriteria[key]?.exclude ?? [];

    // Reviews corrupted by the old item-wise conflict merge hold Other as
    // several items. Join them so the textarea shows everything (an array
    // value renders comma-joined) and the next save restores the single item.
    values[key] =
      key === 'other'
        ? { include: joinOtherItems(include), exclude: joinOtherItems(exclude) }
        : { include, exclude };
  });

  return values;
};

// New values that are entered will come through as a string and existing values will be an array
const valueAsArray = (value: string | string[]) => {
  if (typeof value === 'string') {
    return [value].filter((value) => Boolean(value.trim()));
  }

  return value;
};

// The per-category Include/Exclude editing fields, free of any host chrome:
// hosts own the <form>, submit buttons, spacing, and save wiring (typically
// useSaveCriteria with the same form instance). Rendered as a fragment of
// <fieldset>s so the host's layout container spaces them directly.
// Label/field wiring ids are instance-scoped so instances can coexist in one
// document; only the legend anchor ids (#population etc.) stay global.
// Generic so hosts whose form values extend Criteria (e.g. the Settings
// form's extra protocolLink field) can pass their form without casts.
type Props<TValues extends Criteria> = {
  form: UseFormReturn<TValues>;
  framework?: CriteriaFramework;
  requiresFramework?: boolean;
  // Bump to remount the fields after a conflict merge (see useSaveCriteria):
  // BulletedTextarea freezes its text at mount.
  mergeGeneration?: number;
  // 'sidebar' compacts the fields for the criteria panel.
  layout?: CriteriaFieldsetsLayout;
  disabled?: boolean;
};

const CriteriaFieldsets = <TValues extends Criteria>({
  form,
  framework,
  requiresFramework = false,
  mergeGeneration = 0,
  layout = 'default',
  disabled = false,
}: Props<TValues>) => {
  const { register, control } = form;
  const isSidebar = layout === 'sidebar';
  const idScope = useUniqueID();
  const categories = getFrameworkCategoriesWithFallbackToPicos(
    framework
  ) as Partial<Record<CriteriaCategory, string>>;
  const categoryKeys = getCategoryKeys(framework);

  return (
    <>
      {categoryKeys.map((key) => {
        const label = categories[key] ?? key;
        const includeId = `${idScope}-${key}Include`;
        const includeName = `${key}.include` as BulletPath<TValues>;
        const excludeId = `${idScope}-${key}Exclude`;
        const excludeName = `${key}.exclude` as BulletPath<TValues>;

        return (
          <fieldset
            key={`${key}-${mergeGeneration}`}
            disabled={disabled}
            className={classNames(styles.fieldset, {
              [styles.sidebar]: isSidebar,
            })}
          >
            <CriteriaSectionHeader
              label={label}
              criteriaKey={key}
              requiresFramework={requiresFramework}
              framework={framework}
              layout={layout}
            />

            <div className={styles.row}>
              <Stack className={styles.rowItem} spacing={1}>
                <FormLabel
                  aria-label={`${label} include`}
                  className={styles.includeLabel}
                  id={includeId}
                >
                  Include
                </FormLabel>

                {key === 'other' && (
                  <TextArea
                    aria-labelledby={includeId}
                    className={styles.textarea}
                    {...register(includeName, {
                      setValueAs: valueAsArray,
                    })}
                  />
                )}

                {key !== 'other' && (
                  <BulletedTextarea
                    name={includeName}
                    control={control}
                    ariaLabelledBy={includeId}
                    autoHeight={isSidebar}
                    className={isSidebar ? styles.bulletedInput : undefined}
                    disabled={disabled}
                  />
                )}
              </Stack>

              <Stack className={styles.rowItem} spacing={1}>
                <FormLabel
                  aria-label={`${label} exclude`}
                  className={styles.excludeLabel}
                  id={excludeId}
                >
                  Exclude
                </FormLabel>

                {key === 'other' && (
                  <TextArea
                    aria-labelledby={excludeId}
                    className={styles.textarea}
                    {...register(excludeName, {
                      setValueAs: valueAsArray,
                    })}
                  />
                )}

                {key !== 'other' && (
                  <BulletedTextarea
                    name={excludeName}
                    control={control}
                    ariaLabelledBy={excludeId}
                    autoHeight={isSidebar}
                    className={isSidebar ? styles.bulletedInput : undefined}
                    disabled={disabled}
                  />
                )}
              </Stack>
            </div>
          </fieldset>
        );
      })}
    </>
  );
};

export default CriteriaFieldsets;
